import React, {useState} from 'react';
import TableHeader from 'components/table/TableHeader';
import TableRow from 'components/table/TableRow';
import {useDispatch, useSelector} from 'react-redux';
import {AppDispatch, RootState} from 'store/store';
import TableCell from 'components/table/TableCell';
import {MedicalDocument} from 'types/medicalDocument';
import Table from 'components/table/Table';
import {SearchType} from 'types/search';
import {MedicalDocumentThunks} from './medicalDocumentThunks';
import {
  setIsEditing,
  setMedicalDocument,
  setActiveTab,
} from 'features/medicalDocument/medicalDocumentSlice';
import BlueButton from 'components/BlueButton';
import PlusIcon from 'assets/images/icons/PlusIcon';
import {useDropdownActionMenu} from 'hooks/useDropdownActionMenu';
import TrashIcon from 'assets/images/icons/TrashIcon';
import DotsMenu from 'components/DotsMenu';
import StatusBadge from 'components/StatusBadge';
import usePermissions from '../../hooks/usePermissions';

const MedicalDocumentTable: React.FC<{openDrawer: () => void}> = ({
  openDrawer,
}) => {
  const medicalDocuments = useSelector(
    (state: RootState) => state.medicalDocument.medicalDocuments,
  );
  const dispatch = useDispatch<AppDispatch>();
  const pagy = useSelector((state: RootState) => state.medicalDocument.pagy);
  const activeTab = useSelector(
    (state: RootState) => state.medicalDocument.activeTab,
  );
  const {setDotsMenuIsOpen} = useDropdownActionMenu();
  const {checkPermission} = usePermissions();
  const loading = useSelector(
    (state: RootState) => state.medicalDocument.status === 'loading',
  );

  const [visibleColumns, setVisibleColumns] = useState({
    name: true,
    status: true,
    description: true,
  });

  interface VisibleColumnsType {
    name: boolean;
    status: boolean;
    description: boolean;
  }

  const fetchData = async ({
    page,
    search,
    sortField,
    sortDirection,
    rowsPerPage,
  }: {
    page: number;
    search: SearchType<{[key: string]: {operator: string; value: string}}>;
    sortField: string;
    sortDirection: string;
    rowsPerPage: number;
  }): Promise<void> => {
    if (activeTab === 'Active' || activeTab === 'Inactive') {
      search.where.status = {
        operator: '==',
        value: activeTab.toLowerCase(),
      };
    } else {
      delete search.where.status;
    }
    await dispatch(
      MedicalDocumentThunks.index({
        page,
        search,
        sortField,
        sortDirection,
        rowsPerPage,
      }),
    );
  };

  const handleColumnToggle = (column: string) => {
    if (column in visibleColumns) {
      setVisibleColumns(prev => ({
        ...prev,
        [column]: !prev[column as keyof VisibleColumnsType],
      }));
    }
  };

  const handleOpenDrawer = (id: string) => async () => {
    dispatch(setIsEditing(false));
    openDrawer();
    await dispatch(MedicalDocumentThunks.show(id));
  };

  const handleOpenNewMedicalDocumentDrawer = () => {
    dispatch(
      setMedicalDocument({
        name: '',
        status: '',
        description: '',
      }),
    );
    dispatch(setIsEditing(true));
    openDrawer();
  };

  const searchByOptions = [{label: 'Name', value: 'name', operator: '=='}];

  const columnOptions = [
    {label: 'Name', value: 'name', isVisible: visibleColumns.name},
    {label: 'Status', value: 'status', isVisible: visibleColumns.status},
    {label: 'Notes', value: 'notes', isVisible: visibleColumns.description},
  ];

  const handleDelete = async (id: string) => {
    await dispatch(MedicalDocumentThunks.delete(id));
    setDotsMenuIsOpen(null);
  };
  const handleDeactivate = (medicalDocumentId: string) => {
    dispatch(MedicalDocumentThunks.deactivate(medicalDocumentId));
    setDotsMenuIsOpen(null);
  };

  const handleReactivate = (medicalDocumentId: string) => {
    dispatch(MedicalDocumentThunks.activate(medicalDocumentId));
    setDotsMenuIsOpen(null);
  };

  return (
    <Table
      fetchData={fetchData}
      pagy={pagy}
      pageable={true}
      recordsName={'Documents'}
      searchable={true}
      activeTab={activeTab}
      setActiveTab={(tab: string) => dispatch(setActiveTab(tab))}
      tabs={['All', 'Active', 'Inactive']}
      columns={true}
      loading={loading}
      columnOptions={columnOptions}
      onColumnToggle={handleColumnToggle}
      searchByOptions={searchByOptions}
      blueButton={
        checkPermission('create', 'MedicalDocument') ? (
          <BlueButton
            onClick={handleOpenNewMedicalDocumentDrawer}
            label=""
            icon={<PlusIcon />}
          />
        ) : null
      }>
      <thead>
        <tr>
          {visibleColumns.name && <TableHeader label="Name" sortBy="name" />}
          {visibleColumns.status && (
            <TableHeader label="Status" sortBy="status" />
          )}
          {visibleColumns.description && <TableHeader label="Description" />}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200 dark:divide-neutral-700">
        {medicalDocuments.map((medicalDocument: MedicalDocument) => {
          const userOptions = [];
          if (checkPermission('update', 'MedicalDocument')) {
            userOptions.push({
              label: 'Edit',
              onClick: handleOpenDrawer(medicalDocument.id),
              hoverClass: 'hover:bg-cyan-100',
            });
            if (medicalDocument.status === 'Active') {
              userOptions.push({
                label: 'Deactivate',
                onClick: () => handleDeactivate(medicalDocument.id),
                hoverClass: 'hover:bg-red-100 hover:text-red-800',
              });
            }
            if (medicalDocument.status === 'Inactive') {
              userOptions.push({
                label: 'Activate',
                onClick: () => handleReactivate(medicalDocument.id),
                hoverClass: 'hover:bg-cyan-100',
              });
            }
          }
          if (checkPermission('delete', 'MedicalDocument')) {
            userOptions.push({
              label: 'Delete',
              onClick: () => handleDelete(medicalDocument.id),
              icon: <TrashIcon />,
              hoverClass: 'hover:bg-red-100 hover:text-red-800',
            });
          }
          return (
            <TableRow key={medicalDocument.id}>
              {visibleColumns.name && (
                <TableCell>
                  <div className=" relative group">
                    <div className="w-full flex items-center gap-x-3">
                      <div className="flex-grow">
                        {checkPermission('update', 'MedicalDocument') ? (
                          <span
                            className="text-sm cursor-pointer font-medium dark:text-neutral-200 underline text-cyan-600"
                            onClick={handleOpenDrawer(medicalDocument.id)}>
                            {medicalDocument.name}
                          </span>
                        ) : (
                          <span className="text-sm font-medium text-gray-700 dark:text-neutral-300">
                            {medicalDocument.name}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </TableCell>
              )}
              {visibleColumns.status && (
                <TableCell>
                  <StatusBadge status={medicalDocument.status} />
                </TableCell>
              )}
              {visibleColumns.description && (
                <TableCell className="whitespace-nowrap overflow-hidden text-ellipsis">
                  <div
                    style={{maxWidth: '150px'}}
                    className="whitespace-nowrap overflow-hidden text-ellipsis">
                    {medicalDocument.description}
                  </div>
                </TableCell>
              )}
              <TableCell>
                <DotsMenu options={userOptions} />
              </TableCell>
            </TableRow>
          );
        })}
      </tbody>
    </Table>
  );
};

export default MedicalDocumentTable;
